import React, { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { v4 as uuid } from "uuid";

import { Button, Loading } from "ui-library/atoms";

import i18n from "core/i18n";
import { CancelRequestButton, InPageFormButtonsLayout } from "containers/inPageForm";
import { useHasRole } from "core/hooks";
import { Roles } from "enums";
import { CancellationReason, ChangeStatus, Document, FileItem, FileItemStatusEnum, SignatureStatus, SignatureType } from "types";

import { Recapitulation } from "./components";
import {
    AttachmentsSection,
    DigiSignAuthorizedSigningSection,
    DigiSignOneTimeSigningSection,
    PhysicalSignRequestDocumentSection,
    SignatureTypeSection,
} from "./field";

import { DigiSignStatus, FormStep, VehicleCancellationFormValues } from "../types";
import useAttachments from "../hooks/useAttachments";
import { getIsSuccessUploadStateInEveryFileItem, isAllAttachmentsUploaded } from "../util";

const isSigned = (
    isAuthenticated: boolean,
    signatureType: SignatureType,
    digitalSignatureOnlineStatus: DigiSignStatus,
    digitalSignatureFormStatus: SignatureStatus,
    uploadedSignature: FileItem[],
) =>
    signatureType === SignatureType.ELECTRONIC
        ? !isAuthenticated || digitalSignatureOnlineStatus === "signed" || digitalSignatureFormStatus === SignatureStatus.SIGNED
        : uploadedSignature?.length > 0 && getIsSuccessUploadStateInEveryFileItem(uploadedSignature);

type Step2ReviewCompleteProps = {
    changeStatus: ChangeStatus;
    cancellationReasons: CancellationReason[];
    digitalSignatureFormStatus?: SignatureStatus;
    digitalSignatureOnlineStatus: DigiSignStatus;
    setFormStep: (formStep: FormStep) => void;
    onSubmitCancellationParams: (data: VehicleCancellationFormValues) => void;
    onCompleteCancellation: (data: VehicleCancellationFormValues) => void;
    isSubmitting: boolean;
};

export const Step2ReviewComplete: FC<Step2ReviewCompleteProps> = ({
    changeStatus,
    cancellationReasons,
    digitalSignatureFormStatus,
    digitalSignatureOnlineStatus,
    setFormStep,
    onSubmitCancellationParams,
    onCompleteCancellation,
    isSubmitting,
}) => {
    const { t } = i18n.useTranslation();
    const isAuthenticated = useHasRole(Roles.AUTHENTICATED);

    const { getValues, setValue, handleSubmit, watch } = useFormContext<VehicleCancellationFormValues>();

    const [changeId, cancellationReason, signatureType] = getValues(["changeSheetId", "cancellationReason", "signatureType"]);

    const [uploadedSignature = [], uploadedAttachments = {}] = watch(["signature", "attachments"]);

    const { attachments, isAttachmentsFetching } = useAttachments(changeId, cancellationReason);

    // Preload the values for form from BE.
    useEffect(() => {
        attachments.forEach((attachment) => {
            const fileItems: FileItem[] = attachment.documents.map((doc: Document) => ({
                id: uuid(),
                uploadId: doc.id,
                fileName: doc.fileName,
                uploadStatus: FileItemStatusEnum.UPLOADED,
            }));

            setValue(`attachments.${attachment.type}`, fileItems);
        });
    }, [attachments]);

    const submitButtonLabel = changeStatus === ChangeStatus.SUBMITTED ? t("change.finalizeRequest") : t("common.continue");

    const isNotSubmittableComplete =
        !isAllAttachmentsUploaded(attachments, uploadedAttachments) ||
        !isSigned(isAuthenticated, signatureType, digitalSignatureOnlineStatus, digitalSignatureFormStatus, uploadedSignature);

    return (
        <>
            <Recapitulation
                cancellationReasons={cancellationReasons}
                attachments={attachments}
                isAttachmentsFetching={isAttachmentsFetching}
            />

            <SignatureTypeSection isDisabled={isSubmitting || changeStatus === ChangeStatus.SUBMITTED} />

            {changeStatus === ChangeStatus.SUBMITTED && (
                <>
                    {attachments?.length > 0 && <AttachmentsSection attachments={attachments} />}

                    {signatureType === SignatureType.PHYSICAL && <PhysicalSignRequestDocumentSection />}

                    {signatureType === SignatureType.ELECTRONIC &&
                        (isAuthenticated ? (
                            <DigiSignAuthorizedSigningSection
                                digitalSignatureOnlineStatus={digitalSignatureOnlineStatus}
                                digitalSignatureFormStatus={digitalSignatureFormStatus}
                            />
                        ) : (
                            <DigiSignOneTimeSigningSection />
                        ))}
                </>
            )}

            <InPageFormButtonsLayout
                leftButton={
                    changeStatus === ChangeStatus.SUBMITTED ? (
                        <CancelRequestButton changeSheetId={changeId} />
                    ) : (
                        <Button variant="outlined" color="blue" onClick={() => setFormStep(FormStep.S1_CANCELLATION_PARAMS)}>
                            {t("common.back")}
                        </Button>
                    )
                }
                rightButton={
                    <Button
                        variant="contained"
                        color="blue"
                        onClick={
                            changeStatus === ChangeStatus.SUBMITTED
                                ? handleSubmit(onCompleteCancellation)
                                : handleSubmit(onSubmitCancellationParams)
                        }
                        disabled={isSubmitting || (changeStatus === ChangeStatus.SUBMITTED && isNotSubmittableComplete)}
                    >
                        {isSubmitting ? <Loading loading /> : submitButtonLabel}
                    </Button>
                }
            />
        </>
    );
};
